// ============================================
// Distribuidores.jsx — Programa de distribuidoras asociadas
// Physiology New Look · Medusa v2
// ============================================

import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCustomer } from '../context/CustomerContext';
import {
  applyDistributor,
  getMyDistributorStatus,
} from '../services/medusa/distributor';
import SEO from '../components/SEO';
import PhysDialog from '../components/PhysDialog';
import './Distribuidores.css';

const BG_IMG =
  '/images/secciones/Socios/Portada_background_physiology_skincare_cosmetic_ingredientes_mx_55.webp';

const PERKS = [
  {
    title: 'Precios exclusivos',
    body: 'Lista de precios socia visible en cada producto, lado a lado con el público.',
  },
  {
    title: 'Pedido mínimo $20,000',
    body: 'Tu carrito acumula al precio socio. Liberamos el pago cuando llegas al mínimo.',
  },
  {
    title: 'Regalos por monto',
    body: 'A cada tier que cruzas se suman regalos al carrito de forma automática.',
  },
  {
    title: 'Panel propio',
    body: 'Tu cuenta gana secciones de programa, lista de precios y pedidos.',
  },
];

const STATUS_COPY = {
  pending: {
    title: 'Tu solicitud está en revisión',
    body: 'Te avisaremos por correo cuando la aprobemos. Suele tomar 1–3 días hábiles.',
    badge: 'En revisión',
  },
  approved: {
    title: 'Bienvenida al programa',
    body: 'Ya puedes ver precios socio en la tienda y armar pedidos al precio exclusivo.',
    badge: 'Aprobada',
  },
  rejected: {
    title: 'Tu solicitud no procedió',
    body: 'Si crees que es un error o quieres reactivar el proceso, escríbenos.',
    badge: 'Rechazada',
  },
};

export default function Distribuidores() {
  const { customer, token } = useCustomer() || {};
  const navigate = useNavigate();

  const [statusLoading, setStatusLoading] = useState(false);
  const [status, setStatus] = useState(null);

  const [form, setForm] = useState({
    professional_type: '',
    company_name: '',
    rfc: '',
    contact_name: '',
    email: '',
    phone: '',
    city: '',
    estimated_volume: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitOk, setSubmitOk] = useState(false);

  useEffect(() => {
    if (customer?.email && !form.email) {
      setForm((f) => ({
        ...f,
        email: customer.email,
        contact_name: customer.first_name
          ? `${customer.first_name} ${customer.last_name || ''}`.trim()
          : f.contact_name,
      }));
    }
  }, [customer]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!token) return;
      setStatusLoading(true);
      try {
        const data = await getMyDistributorStatus(token);
        if (!cancelled) setStatus(data);
      } catch (e) {
        if (!cancelled && e.status !== 401) {
          // 401 esperado si la sesión expiró; cualquier otro error lo silenciamos
          // para no romper la landing pública.
        }
      } finally {
        if (!cancelled) setStatusLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const existingStatus = useMemo(() => {
    if (status?.is_distributor) return 'approved';
    return status?.application?.status || null;
  }, [status]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    setSubmitOk(false);

    if (!form.professional_type) {
      setSubmitError('Indica si eres médico, cosmetóloga o spa.');
      return;
    }
    if (
      !form.company_name.trim() ||
      !form.contact_name.trim() ||
      !form.email.trim()
    ) {
      setSubmitError('Completa establecimiento, contacto y correo.');
      return;
    }

    setSubmitting(true);
    try {
      await applyDistributor(form, token);
      setSubmitOk(true);
      // Limpia el formulario; el aviso (PhysDialog) lleva a tienda o inicio.
      setForm((f) => ({
        ...Object.fromEntries(Object.keys(f).map((k) => [k, ''])),
      }));
      if (token) {
        const next = await getMyDistributorStatus(token).catch(() => null);
        if (next) setStatus(next);
      }
    } catch (err) {
      setSubmitError(err?.message || 'No pudimos enviar tu solicitud.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStatus = () => {
    const copy = STATUS_COPY[existingStatus];
    if (!copy) return null;
    return (
      <section className="distributors-status">
        <span className={`badge ${existingStatus}`}>{copy.badge}</span>
        <h2>{copy.title}</h2>
        <p>{copy.body}</p>
        {existingStatus === 'approved' && (
          <button
            type="button"
            className="distributors-submit"
            style={{ marginTop: '2rem', maxWidth: 280 }}
            onClick={() => navigate('/shop')}
          >
            Ir a la tienda
          </button>
        )}
      </section>
    );
  };

  const renderForm = () => (
    <section className="distributors-form-wrap">
      <h2>Solicita tu acceso</h2>
      <p className="lead">
        Cuéntanos de tu negocio. Revisamos cada solicitud y respondemos por correo
        en 1–3 días hábiles.
      </p>
      <form className="distributors-form" onSubmit={handleSubmit} noValidate>
        <fieldset className="full distributors-professional">
          <legend>¿Cuál es tu perfil profesional? *</legend>
          <div className="distributors-radios">
            {[
              { value: 'medico', label: 'Médico(a)' },
              { value: 'cosmetologa', label: 'Cosmetóloga' },
              { value: 'spa', label: 'Spa' },
            ].map((opt) => (
              <label key={opt.value} className="distributors-radio">
                <input
                  type="radio"
                  name="professional_type"
                  value={opt.value}
                  checked={form.professional_type === opt.value}
                  onChange={handleChange}
                />
                <span>{opt.label}</span>
              </label>
            ))}
          </div>
        </fieldset>
        <label>
          Nombre del establecimiento *
          <input
            name="company_name"
            value={form.company_name}
            onChange={handleChange}
            required
          />
        </label>
        <label>
          RFC
          <input name="rfc" value={form.rfc} onChange={handleChange} />
        </label>
        <label>
          Contacto *
          <input
            name="contact_name"
            value={form.contact_name}
            onChange={handleChange}
            required
          />
        </label>
        <label>
          Correo *
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            required
          />
        </label>
        <label>
          Teléfono
          <input name="phone" value={form.phone} onChange={handleChange} />
        </label>
        <label>
          Ciudad
          <input name="city" value={form.city} onChange={handleChange} />
        </label>
        <label className="full">
          Volumen estimado mensual
          <input
            name="estimated_volume"
            value={form.estimated_volume}
            onChange={handleChange}
            placeholder="Ej: 30,000 MXN / 50 piezas"
          />
        </label>
        <label className="full">
          ¿Algo más que debamos saber?
          <textarea
            name="message"
            value={form.message}
            onChange={handleChange}
          />
        </label>

        {submitError && (
          <div className="distributors-message error">{submitError}</div>
        )}
        <button
          type="submit"
          className="distributors-submit"
          disabled={submitting}
        >
          {submitting ? 'Enviando…' : 'Enviar solicitud'}
        </button>
      </form>
    </section>
  );

  return (
    <div
      className="distributors-page"
      data-header-theme="light"
      style={{ '--distributors-bg-img': `url('${BG_IMG}')` }}
    >
      <SEO
        title="Programa de distribuidoras Physiology"
        description="Únete al programa de distribuidoras de Physiology Skincare Co. Precios exclusivos, soporte y herramientas para crecer tu negocio de cuidado facial."
        path="/distribuidores"
      />
      <section className="distributors-hero">
        <div className="eyebrow">Programa de distribuidoras</div>
        <h1>Acceso exclusivo para socias.</h1>
        <p>
          Precios especiales, regalos por monto y un panel propio para gestionar
          tu relación con la marca.
        </p>
      </section>

      <section className="distributors-perks">
        {PERKS.map((p) => (
          <div key={p.title} className="distributors-perk">
            <h3>{p.title}</h3>
            <p>{p.body}</p>
          </div>
        ))}
      </section>

      {statusLoading ? null : existingStatus ? renderStatus() : renderForm()}

      <PhysDialog
        isOpen={submitOk}
        onClose={() => setSubmitOk(false)}
        showCheck
        title="¡Solicitud enviada!"
        message="Recibimos tu solicitud. Revisamos cada una con calma y te avisamos por correo en 1–3 días hábiles."
        primary={{ label: 'Ir a la tienda', onClick: () => navigate('/shop') }}
        secondary={{ label: 'Volver al inicio', onClick: () => navigate('/') }}
      />
    </div>
  );
}
